import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Caption1, Checkbox, InputUrl, Label } from "@activecollab/components";
import classNames from "classnames";
import { isEqual } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import queryString from "query-string";
import { Header } from "~/components/CalendarEvent/Header";
import { useSecurity } from "~/components/Hooks";
import {
  Attachments,
  Comments,
  DescriptionWrapper,
  ObjectPropertiesWrapper,
  ObjectSubscribersPanel,
  ObjectWrapper,
} from "~/components/ModalObjects";
import ObjectHistory from "~/components/ObjectHistory/ObjectHistory";
import { editCalendarEvent, fetchCalendarEvent } from "~/store/actions/actions.calendarEvents";
import { getCalendarByEventId, getCalendarEventById } from "~/store/selectors/selector.calendarEvents";
import { getUserTimezone } from "~/store/selectors/selector.configOptions";
import { canAccessWorkload, isLoggedUserClientSelector } from "~/store/selectors/selector.users";
import CalendarEventFormContainer from "./CalendarEventFormContainer";
import { CalendarEventParticipants } from "./CalendarEventParticipants";
import { CalendarEventRepeatProperty } from "./CalendarEventRepeatProperty";
import { useCalendarEventRescheduleContext } from "./CalendarEventRescheduleContext";
import { CalendarEventSelectDate } from "./CalendarEventSelectDate";
import { ExternalCalendarEventDetails } from "./ExternalCalendarEventDetails";
import { isAllDay, isRepeating, makeEventForDate } from "../Calendar/calendarUtils";
import NewCommentsNotification from "../comments/NewCommentsNotification";
import NotFoundSlate from "../EmptySlate/NotFoundSlate";
import RemindersContainer from "../Reminders/RemindersContainer";
import { showErrorToast } from "../Toast";

const NotFound = () => {
  const { t } = useTranslation();
  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-mb-8">
      <NotFoundSlate
        header={t("This event is not found")}
        pageName={t("calendar page")}
        pageLink="/calendar"
      />
    </div>
  );
};

export const CalendarEvent = ({ id, occurrence }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const timezone = useSelector(getUserTimezone);
  const userCanAccessWorkload = useSelector(canAccessWorkload);
  const { setEvent, rescheduleEvent } = useCalendarEventRescheduleContext();
  const _event = useSelector((state) => getCalendarEventById(state, id));
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);

  const { search } = useLocation();
  const params = queryString.parse(search);
  const event = useMemo(() => {
    if (occurrence) {
      return makeEventForDate(_event, timezone, occurrence);
    }
    return makeEventForDate(_event, timezone, params?.date);
  }, [_event, occurrence, params?.date, timezone]);

  const calendar = useSelector((state) => getCalendarByEventId(state, id));
  const can = useSecurity();
  const canEdit = can(["edit"], event);
  const [edit, setEdit] = useState(false);

  const handleEdit = useCallback(() => {
    setEdit(true);
  }, [setEdit]);

  const handleSave = useCallback(
    (startOn, endsOn, startOnTime, endsOnTime) => {
      const allDay = startOnTime === null && endsOnTime === null;

      return rescheduleEvent({
        starts_on: startOn,
        ends_on: endsOn,
        starts_on_time: startOnTime,
        ends_on_time: endsOnTime,
        reference_timezone: allDay ? "UTC" : timezone,
      });
    },
    [rescheduleEvent, timezone]
  );

  const updateParticipants = useCallback(
    (selected) => {
      if (selected.length === 0) {
        showErrorToast(t("Events need to have at least one participant."));
      } else {
        if (!isEqual(event.participants, selected)) {
          dispatch(
            editCalendarEvent(id, calendar?.id, {
              participants: selected,
            })
          ).then((response) => {
            if (response?.error) {
              showErrorToast(t("Unable to update event."));
            }
          });
        }
      }
    },
    [calendar, dispatch, id, event, t]
  );

  const onRepeatEventChange = useCallback(
    (attributes) => {
      rescheduleEvent(attributes);
    },
    [rescheduleEvent]
  );

  const onClose = useCallback(() => {
    setEdit(false);
  }, []);

  const onDoubleClick = useCallback(() => {
    if (canEdit && calendar?.class !== "ExternalCalendar") {
      setEdit(true);
    }
  }, [calendar, canEdit]);

  useEffect(() => {
    return () => {
      dispatch(fetchCalendarEvent(calendar?.id, id));
    };
  }, [calendar?.id, dispatch, id]);

  useEffect(() => {
    if (event) {
      const allDay = isAllDay(event);
      const startsOn = allDay ? moment.unix(event.starts_on).utc() : moment.unix(event.starts_on);
      const endsOn = allDay ? moment.unix(event.ends_on).utc() : moment.unix(event.ends_on);
      const recurrence_instance = event.recurrence_instance
        ? event.recurrence_instance
        : isRepeating(event)
        ? moment.unix(event.starts_on).utc().format()
        : null;
      setEvent({
        id: event.id,
        calendar_id: event.calendar_id,
        repeat_event: event.repeat_event,
        starts_on: startsOn.format("YYYY-MM-DD"),
        ends_on: endsOn.format("YYYY-MM-DD"),
        starts_on_time: allDay ? null : startsOn.format("HH:mm:ss"),
        ends_on_time: allDay ? null : endsOn.format("HH:mm:ss"),
        reference_timezone: event.reference_timezone,
        recurrence_instance: recurrence_instance,
      });
    }
  }, [event, params?.date, setEvent]);

  const toggleVisibleOnWorkload = useCallback(
    (event) => {
      dispatch(
        editCalendarEvent(id, calendar?.id, {
          is_visible_on_workload: event.target.checked,
        })
      );
    },
    [calendar?.id, dispatch, id]
  );

  if (event === null) {
    return <NotFound />;
  }

  return !edit ? (
    <div className="calendar-event-single tw-w-full tw-flex tw-flex-col tw-min-h-full">
      <Header
        onEdit={handleEdit}
        id={id}
        occurrence={occurrence}
      >
        <div
          className={classNames(
            "tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-center tw-w-full tw-gap-10",
            { "tw-gap-4": calendar.class === "ExternalCalendar" }
          )}
        >
          <CalendarEventSelectDate
            startsOn={event.starts_on}
            endsOn={event.ends_on}
            startsOnTime={event.starts_on_time}
            endsOnTime={event.ends_on_time}
            onSave={handleSave}
            canEdit={canEdit && calendar?.class !== "ExternalCalendar"}
          />
          {calendar.class === "ExternalCalendar" ? (
            <ExternalCalendarEventDetails
              calendar={calendar}
              event={event}
            />
          ) : (
            <CalendarEventRepeatProperty
              repeatEvent={event?.repeat_event}
              repeatUntil={event?.repeat_until}
              canEdit={canEdit}
              disableDaysBefore={moment.utc(`${moment.unix(event.starts_on).format("YYYY-MM-DD")} 00:00:00`).unix()}
              onChange={onRepeatEventChange}
            />
          )}
        </div>
      </Header>

      <div className="tw-flex tw-flex-1">
        <ObjectWrapper>
          <div
            className={classNames("tw-relative", {
              "tw-px-6 tw-pt-6": event?.body_formatted || event?.attachments?.length > 0,
            })}
            data-testid="calendar-event-body"
          >
            <NewCommentsNotification
              parentType="CalendarEvent"
              parentId={id}
            />
            <DescriptionWrapper
              parentId={id}
              parentType="Discussion"
              body={event?.body_formatted}
              className={classNames(
                "tw-mb-4 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary",
                { "tw-pb-4 ": event?.body_formatted },
                { "tw-border-none": event?.body_formatted?.length === 0 }
              )}
              onDoubleClick={onDoubleClick}
            />
            <Attachments
              id={id}
              type="CalendarEvent"
              className="tw-mb-4 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary"
            />
          </div>
          <ObjectPropertiesWrapper>
            <div className="tw-flex tw-flex-col tw-gap-6">
              <div className="tw-h-8 tw-flex tw-justify-between tw-items-center">
                <Caption1 weight="bold">{t("Participants")}</Caption1>
                <CalendarEventParticipants
                  id={id}
                  canEdit={canEdit && calendar?.class !== "ExternalCalendar"}
                  calendarId={calendar?.id}
                  participants={event.participants}
                  onClose={updateParticipants}
                  allowAnonymous={calendar?.class === "ExternalCalendar"}
                />
              </div>
              {userCanAccessWorkload ? (
                <div className="tw-flex tw-gap-[6px] tw-min-h-5 tw-items-center">
                  <Checkbox
                    id="is_visible_on_workload"
                    checked={event.is_visible_on_workload}
                    onChange={toggleVisibleOnWorkload}
                    disabled={!canEdit}
                  />
                  <Label
                    htmlFor="is_visible_on_workload"
                    className="tw-cursor-pointer"
                    weight="regular"
                  >
                    {t("Add to participants' workload")}
                  </Label>
                </div>
              ) : null}

              {event?.meeting_url ? (
                <div
                  className={classNames("tw-h-[44px] tw-flex tw-flex-col tw-justify-between", {
                    "tw-h-[52px]": edit,
                  })}
                >
                  <Caption1 weight="bold">{t("Meeting Link")}</Caption1>
                  {edit ? (
                    <InputUrl
                      className="tw-w-auto"
                      value={event.meeting_url}
                    />
                  ) : (
                    <a
                      className="tw-text-primary tw-cursor-pointer tw-no-underline tw-truncate"
                      target="_blank"
                      rel="noreferrer"
                      href={event.meeting_url}
                    >
                      {event.meeting_url}
                    </a>
                  )}
                </div>
              ) : null}
              <div>
                <ObjectSubscribersPanel
                  parentId={id}
                  parentType="CalendarEvent"
                  canEdit={canEdit}
                  members={calendar?.members}
                />
              </div>
              {!isLoggedUserClient && calendar && calendar?.class !== "ExternalCalendar" ? (
                <RemindersContainer
                  parent={_event}
                  members={calendar?.members}
                />
              ) : null}
            </div>
          </ObjectPropertiesWrapper>
          <div className="tw-px-6">
            <Comments
              parentId={id}
              parentType="CalendarEvent"
              commentsCount={event?.comments_count}
              projectId={0}
              userIds={calendar ? calendar.members : []}
            />
            <ObjectHistory
              objectId={id}
              objectType="CalendarEvent"
            />
          </div>
        </ObjectWrapper>
      </div>
    </div>
  ) : (
    <CalendarEventFormContainer
      calendarEventId={id}
      startsOn={moment.unix(event.starts_on).format("YYYY-MM-DD")}
      endsOn={moment.unix(event.ends_on).format("YYYY-MM-DD")}
      startsOnTime={event.starts_on_time ? moment.unix(event.starts_on).format("HH:mm") : null}
      endsOnTime={event.ends_on_time ? moment.unix(event.ends_on).format("HH:mm") : null}
      occurrence={occurrence}
      onClose={onClose}
    />
  );
};

CalendarEvent.displayName = "CalendarEvent";
CalendarEvent.propTypes = {
  id: PropTypes.number.isRequired,
  occurrence: PropTypes.number,
};
